import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { assertIsUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import type { WorkspaceBase } from '@bizzdesign/data-access-workspaces';
import {
  ContentOpenService,
  CreateWorkspaceFrameNavigationState,
} from '@bizzdesign/feature-content-open';
import { baseRoutes } from '@bizzdesign/shared/utils';
import { from, type Observable } from 'rxjs';
import type { NavigationApi } from '@bizzdesign/ai-assistant/data-access';
import type {
  CreateFrameData,
  NavigateData,
  NavigatePage,
} from '@bizzdesign/ai-assistant/workflows';

function pageRouteCommands(page: NavigatePage): string[] {
  switch (page) {
    case 'home':
      return [baseRoutes.home];
    case 'search':
      return [baseRoutes.home, 'search'];
    case 'workspaces':
      return [baseRoutes.workspaces];
    case 'archive':
      return [baseRoutes.archive];
    case 'data-management':
      return [baseRoutes.dataManagement];
  }
}

function targetCommands(data: NavigateData): string[] {
  switch (data.kind) {
    case 'page':
      return pageRouteCommands(data.page);
    case 'view':
      return [baseRoutes.views, data.viewId];
    case 'workspace':
      return [baseRoutes.workspaces, data.workspaceId];
    case 'frame':
      return [baseRoutes.frames, data.frameId];
  }
}

/**
 * Performs the in-app navigation the assistant requests via the
 * `navigate_to_page` and `create_frame` tools. The assistant container registers
 * this as the store's {@link NavigationApi}; content targets reuse
 * {@link ContentOpenService}, new frames go through the same `frames/create`
 * route the workspace UI uses, and existing frames use their direct route. A
 * failed navigation surfaces an error toast instead of failing silently.
 */
@Injectable({ providedIn: 'root' })
export class AssistantNavigationService implements NavigationApi {
  private readonly router = inject(Router);
  private readonly contentOpenService = inject(ContentOpenService);
  private readonly messageService = inject(MessageService);

  /**
   * Takes the user to the destination the assistant chose, routing pages through
   * the {@link Router} and views/workspaces through {@link ContentOpenService}.
   *
   * @param data - The destination streamed by the assistant.
   * @returns Nothing; a navigation that fails surfaces an error toast instead of
   * rejecting, since the caller is a stream handler with nowhere to report to.
   */
  navigateTo(data: NavigateData): void {
    const targetUrl = this.router.serializeUrl(
      this.router.createUrlTree(targetCommands(data)),
    );
    try {
      this.resolveNavigation$(data).subscribe({
        next: (didNavigate) => {
          // false = a blocked navigation, unless we are already on the target
          // (a benign no-op same-URL skip, where the URL is unchanged).
          if (!didNavigate && this.router.url !== targetUrl) {
            this.notifyNavigationFailed(data.label);
          }
        },
        error: () => this.notifyNavigationFailed(data.label),
      });
    } catch {
      this.notifyNavigationFailed(data.label);
    }
  }

  /**
   * Creates a new design frame in the given workspace and opens it, via the same
   * `frames/create` route the workspace UI uses.
   *
   * @param data - Target workspace, and the frame name when the assistant chose
   * one; without a name the frame is created as "Untitled" and the user is
   * prompted to rename it.
   * @returns Nothing; a failure surfaces an error toast, as with {@link navigateTo}.
   */
  createFrame(data: CreateFrameData): void {
    const label =
      data.name !== undefined
        ? `"${data.name}" in ${data.workspaceName}`
        : `a new frame in ${data.workspaceName}`;
    try {
      assertIsUUID(data.workspaceId);
      assertIsUUID(data.frameId);
      const workspace: WorkspaceBase = {
        id: data.workspaceId,
        name: data.workspaceName,
        archived: false,
      };
      from(
        this.router.navigate([baseRoutes.frames, 'create'], {
          state: new CreateWorkspaceFrameNavigationState(
            workspace,
            data.name,
            data.frameId,
          ),
        }),
      ).subscribe({
        next: (didNavigate) => {
          if (!didNavigate) {
            this.notifyNavigationFailed(label);
          }
        },
        error: () => this.notifyNavigationFailed(label),
      });
    } catch {
      this.notifyNavigationFailed(label);
    }
  }

  private resolveNavigation$(data: NavigateData): Observable<boolean> {
    switch (data.kind) {
      case 'page':
        return from(this.router.navigate(pageRouteCommands(data.page)));
      case 'view':
        return this.contentOpenService.openView(data.viewId, data.label);
      case 'workspace':
        return this.contentOpenService.openWorkspace(
          data.workspaceId,
          data.label,
        );
      case 'frame':
        return from(this.router.navigate([baseRoutes.frames, data.frameId]));
    }
  }

  private notifyNavigationFailed(label: string): void {
    this.messageService.add({
      severity: 'error',
      summary: `Couldn't open ${label}`,
      detail: 'Something went wrong while navigating. Please try again.',
    });
  }
}
